import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { AppConfigService } from '../appConfig/appConfigService';
import { LogParameters } from './interfaces/log.parameters';
import { BaseTimber } from '@core/base-classes/base-timber';
import { LocalStorageService } from '@core/localStorage.service';
import { CookieService } from '@core/cookie.service';
import { SessionInfo } from 'app/services/authentication/auth.service';
import { Consts } from '@shared/consts';
import { BiEvent, IApiReportEvent, IErrorReportEvent, ISessionStartEvent, ISessionTagsEvent, IStageReportEvent, ITags, IUiNavigationEvent, IUserActionEvent } from './interfaces/bi.events';
import { BiEventType } from './interfaces/bi.enums';
import { RoleTypeEnum } from '@shared/enums';
import { ILoggerService } from '@nodeModules/@itero/itero-platform-login';
import { Version } from '../../../environments/app-version';

@Injectable({ providedIn: 'root' })
export class TimberService extends BaseTimber implements OnDestroy, ILoggerService {
	private pageInstanceId: string;
	private readonly logSessionInstanceId: string;
	private readonly browserInstanceId: string;

	private static readonly SESSION_INSTANCE_ID_KEY = 'logSessionInstanceId';
	private static readonly BROWSER_INSTANCE_ID_COOKIE = 'browserInstanceId';
	private static readonly BROWSER_INSTANCE_ID_EXPIRY_DAYS = 365 * 10; // 10 years

	constructor(protected appConfigService: AppConfigService,
							protected localStorageService: LocalStorageService,
							private cookieService: CookieService,
							private router: Router
							) {
		super(appConfigService, localStorageService);
		this.pageInstanceId = this.generateUniqueId();
		this.logSessionInstanceId = this.getOrCreateSessionInstanceId();
		this.browserInstanceId = this.getOrCreateBrowserInstanceId();
		this.subscribeToRouteChanges();
		this.initConsoleLogFlag();
	}

	initialize(loggedInContactId: number, selectedCompanyId: number) {
		super.createLogger(loggedInContactId, selectedCompanyId);
		super.createBi(loggedInContactId, selectedCompanyId);
	}

	private static readonly CONSOLE_LOG_FLAG = 'timber_console_log';
	private consoleLogEnabled = false;

	private initConsoleLogFlag(): void {
		try {
			this.consoleLogEnabled = localStorage.getItem(TimberService.CONSOLE_LOG_FLAG) === 'true';
			window.addEventListener('storage', (event) => {
				if (event.key === TimberService.CONSOLE_LOG_FLAG) {
					this.consoleLogEnabled = event.newValue === 'true';
				}
			});
		} catch {
			this.consoleLogEnabled = false;
		}
	}

	private consoleLog(level: string, message: string, parameters: LogParameters): void {
		if (!this.consoleLogEnabled) { return; }
		try {
			const mod = parameters?.module ? `[${parameters.module}]` : '';
			console.log(`[Timber:${level}]${mod} ${message}`, parameters?.extendedParameters ?? '');
		} catch { }
	}

	///write to log
	trace(message: string, parameters: LogParameters): void { this.consoleLog('TRACE', message, parameters); if (!this.loggerTimber) { return; } this.loggerTimber.trace(message, this.getLogParameters(parameters)); }

	debug(message: string, parameters: LogParameters): void { this.consoleLog('DEBUG', message, parameters); if (!this.loggerTimber) { return; } this.loggerTimber.debug(message, this.getLogParameters(parameters)); }

	info(message: string, parameters: LogParameters): void { this.consoleLog('INFO', message, parameters); if (!this.loggerTimber) { return; } this.loggerTimber.info(message, this.getLogParameters(parameters)); }

	warn(message: string, parameters: LogParameters): void { this.consoleLog('WARN', message, parameters); if (!this.loggerTimber) { return; } this.loggerTimber.warn(message, this.getLogParameters(parameters)); }

	error(message: string, parameters: LogParameters): void { this.consoleLog('ERROR', message, parameters); if (!this.loggerTimber) { return; } this.loggerTimber.error(message, this.getLogParameters(parameters)); }

	fatal(message: string, parameters: LogParameters): void { this.consoleLog('FATAL', message, parameters); if (!this.loggerTimber) { return; } this.loggerTimber.fatal(message, this.getLogParameters(parameters)); }
	
	getLogParameters = (parameters: LogParameters): LogParameters => {
		const sessionId = this.localStorageService.getItemOrEmpty<SessionInfo>(Consts.Storage.SessionInfo).sessionId || 'EMPTY';
		const sessionType = this.localStorageService.getItemOrEmpty<SessionInfo>(Consts.Storage.SessionInfo).sessionType || 'EMPTY';
		return {
			...parameters, extendedParameters: {
				...parameters?.extendedParameters,
				sessionId, sessionType,
				pageInstanceId: this.pageInstanceId,
				logSessionInstanceId: this.logSessionInstanceId,
				browserInstanceId: this.browserInstanceId,
				currentUrl: window.location.href,
				appVersion: Version.version,
				routePath: this.router?.url || 'unknown',
				timestamp: new Date().toISOString()
			}
		}
	}

	logUserActionToTimber({ params , module, traceTitle, contactId, companyId } : { params: object , module: string, traceTitle: string, contactId: number, companyId: number  }): void {
		const sessionInfo = this.localStorageService.getItemOrEmpty<SessionInfo>(Consts.Storage.SessionInfo);
		this.info(traceTitle , {
			module,
			extendedParameters: {
			    ...params,
				userId: `${contactId}`,
				companyId: `${companyId}`,
				sessionId: sessionInfo?.sessionId,
				sessionType: Consts.SessionType,
			},
		});
	}

   //Bi methods
	sessionStartEvent(user: number, companyId: number, roleType: RoleTypeEnum): void {
		this.sendBiEvent({
			event: {
				externalSessionId: '',
				tags: {
					userId: user.toString(),
					companyId: companyId.toString(),
					midcMode: this.getMidcMode(roleType),
				} as ITags
			} as ISessionStartEvent,
			type: BiEventType.SessionStart
		});
	}
	
	userActionEvent(event: IUserActionEvent) : void {
		this.sendBiEvent({
			event,
			type: BiEventType.UserAction
		} as BiEvent);
	}
	
	uiNavigationEvent(event: IUiNavigationEvent): void {
		this.sendBiEvent({
			event,
			type: BiEventType.UiNavigation
		} as BiEvent);
	}
	
	errorReportEvent(event: IErrorReportEvent): void {
		this.sendBiEvent({
			event,
			type: BiEventType.ErrorReport
		} as BiEvent);
	}
	
	apiReportEvent(event: IApiReportEvent): void {
		this.sendBiEvent({
			event,
			type: BiEventType.APIReport
		} as BiEvent);
	}
	
	stageReportEvent(event: IStageReportEvent): void {
		this.sendBiEvent({
			event,
			type: BiEventType.StageReport
		} as BiEvent);
	}

	sessionTagsEvent(event: ISessionTagsEvent): void {
		this.sendBiEvent({
			event,
			type: BiEventType.SessionTags
		} as BiEvent);
	}
	
	LogUserActionAndNavigate(userActionEvent: IUserActionEvent, uiNavigationEvent: IUiNavigationEvent): void {
		this.userActionEvent(userActionEvent);
		this.uiNavigationEvent(uiNavigationEvent);
	}
	
	private getMidcMode(roleType: RoleTypeEnum): string {
		switch (roleType) {
			case RoleTypeEnum.Doctor:
				return 'doctor';
			case RoleTypeEnum.Lab:
				return 'lab';
			default:
				return null;
		}
	}

	private subscribeToRouteChanges(): void {
		if (!this.router) {
			console.warn('TimberService: Router not available, pageInstanceId will not refresh on navigation');
			return;
		}
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			takeUntil(this.componentAlive$)
		).subscribe(() => {
			this.pageInstanceId = this.generateUniqueId();
		});
	}

	private getOrCreateSessionInstanceId(): string {
		try {
			const stored = localStorage.getItem(TimberService.SESSION_INSTANCE_ID_KEY);
			if (stored) {
				return stored;
			}
			const newId = this.generateUniqueId();
			localStorage.setItem(TimberService.SESSION_INSTANCE_ID_KEY, newId);
			return newId;
		} catch (e) {
			return `error:localStorage-${e?.message || 'unknown'}`;
		}
	}

	private getOrCreateBrowserInstanceId(): string {
		try {
			const stored = this.cookieService.get(TimberService.BROWSER_INSTANCE_ID_COOKIE);
			if (stored) {
				return stored;
			}
			const newId = this.generateUniqueId();
			this.cookieService.set(TimberService.BROWSER_INSTANCE_ID_COOKIE, newId, TimberService.BROWSER_INSTANCE_ID_EXPIRY_DAYS);
			return newId;
		} catch (e) {
			return `error:cookie-${e?.message || 'unknown'}`;
		}
	}

	private generateUniqueId(): string {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID();
		}
		const timestamp = Date.now().toString(36);
		const randomBytes = new Uint8Array(8);
		crypto.getRandomValues(randomBytes);
		const randomPart = Array.from(randomBytes, b => b.toString(36)).join('').substring(0, 10);
		return `${timestamp}-${randomPart}`;
	}
}